import _ from "lodash";
import {
  createResource,
  deleteResource,
  determinePaginationParams,
  readUrl,
  readUrlObjectBased,
  updateResourcePath,
  determineSelector,
  getResource,
  updateResource,
} from "./api";

// query/exclude/select mirror experts.getAll's shape (a picker's search + exclusion-set + column
// projection needs). Pagination keeps its pre-existing 10-row default and honors both the legacy
// `per_page` spelling and the camelCase `perPage` other callers use.
const getAll = async (options = {}) => {
  const { exclude, query, page = 1, perPage, per_page: perPageLegacy, ...otherOptions } = options;
  const paginationParams = determinePaginationParams({
    page,
    per_page: perPage ?? perPageLegacy ?? 10,
    returnType: "object",
  });

  let selector = { order: "name", ...otherOptions, ...paginationParams };
  if (!_.isEmpty(query)) selector = { ...selector, name: `ilike.*${query}*` };
  if (!_.isEmpty(exclude)) selector = { ...selector, id: `not.in.(${exclude.join(",")})` };

  const url = readUrl("products", { selector: new URLSearchParams(selector).toString() });
  return getResource(url);
};

// RLS drops any id the viewer may not see. `select` narrows the columns (e.g. "id,price,description").
const getAllById = async (ids, { select } = {}) => {
  const url = readUrlObjectBased("products", {
    conditionParams: { id: `in.(${ids.join(",")})`, ...(select && { select }) },
  });
  return getResource(url);
};

const get = async (id) => {
  const selector = determineSelector(id, { keyId: "id", keySlug: "slug" });
  const url = readUrl("products", { selector });
  return getResource(url);
};

const updateById = async (id, attributes) => {
  const url = updateResourcePath("products", id);
  return updateResource(url, { data: { attributes } });
};

// The profile-image trio mirrors companies.js exactly — same nested `profile_image` route, same
// `{ data } | { error }` result — because <ProfileImage> routes through whichever of them owns the
// entity it was handed and can only do that if they answer alike.
const profileImagePath = (productId) => `${updateResourcePath("products", productId)}/profile_image`;

const createProfileImageById = async (productId, attributes) => {
  return createResource(profileImagePath(productId), { data: { attributes } });
};

const updateProfileImageById = async (productId, attributes) => {
  return updateResource(profileImagePath(productId), { data: { attributes } });
};

const deleteProfileImageById = async (productId) => {
  return deleteResource(profileImagePath(productId));
};

export default {
  createProfileImageById,
  deleteProfileImageById,
  get,
  getAll,
  getAllById,
  updateById,
  updateProfileImageById,
};
